import { useMutation, useQuery } from "@tanstack/react-query";

import {
  BOOK_COMPONENT_CATEGORY_IDS,
  CALL_API_ENDPOINT,
  INDIAN_ISD_CODE,
  KH_VERTICAL_ID,
  LEARN_VERTICAL_ID,
  LOGIN_PRODUCT_ID,
  LOGIN_TENANT_ID,
  PLATFORM,
  REQUEST_PRODUCT_ID,
  ROLES_FOR_SEARCHING_AGENT,
  VALID_SALES_ROLE_IDS,
  X_TENANT,
} from "constants/config";
import { DAY, QUERY_RETRY_ATTEMPTS } from "constants/queryCacheTimes";
import fetchJSON from "hooks/restApiClient";
import { getAuthToken } from "utils/tokenUtils";

const calculateBookPrice = (pkg, subscriptionId) => {
  const bookComponentsFromSubscriptions = pkg.packageSubscriptions
    .flatMap((subscription) => subscription.components)
    .filter(
      (component) =>
        BOOK_COMPONENT_CATEGORY_IDS.includes(
          component.componentCategory.componentCategoryId,
        ) &&
        component.bookPrice &&
        component.packageSubscriptionId === subscriptionId,
    )
    .reduce(
      (acc, component) => ({
        ...acc,
        [component.componentId]: component.bookPrice,
      }),
      {},
    );

  const bookComponentsFromPackage = pkg.bookComponents
    .filter(
      (component) =>
        BOOK_COMPONENT_CATEGORY_IDS.includes(
          component.componentCategory.componentCategoryId,
        ) &&
        component.bookPrice &&
        component.packageSubscriptionId === subscriptionId,
    )
    .reduce(
      (acc, component) => ({
        ...acc,
        [component.componentId]: component.bookPrice,
      }),
      {},
    );

  const bookPrices = {
    ...bookComponentsFromSubscriptions,
    ...bookComponentsFromPackage,
  };

  const totalBookPrice = Object.values(bookPrices).reduce(
    (total, price) => total + price,
    0,
  );

  return totalBookPrice;
};

const getAllComponentProductTypeIds = (pkg, subscription) => {
  const subscriptionComponentProductTypeIds = subscription.components.map(
    (component) => component.componentProduct.componentProductTypeId,
  );

  const bookComponentProductTypeIds = pkg.bookComponents
    .filter(
      (component) =>
        component.packageSubscriptionId === subscription.packageSubscriptionId,
    )
    .map((component) => component.componentProduct.componentProductTypeId);

  return [
    ...subscriptionComponentProductTypeIds,
    ...bookComponentProductTypeIds,
  ];
};

const fetchPaymentHeadsForSubscription = async (subscriptionIds) => {
  const url = new URL(
    `${process.env.REACT_APP_PACKAGE_MANAGEMENT_URL}/does-subscription-payment-heads-exist`,
  );

  const payload = {
    subscription_ids: subscriptionIds,
  };

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_PACKAGE_MANAGEMENT_SECRET_KEY}`,
    "Content-Type": "application/json",
  };
  const options = {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  };

  const response = await fetch(url, options);

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};

export const useCheckPaymentHeadsForSubscription = (subscriptionIds) => {
  return useQuery({
    queryKey: ["subscriptionIds", subscriptionIds],
    queryFn: () => fetchPaymentHeadsForSubscription(subscriptionIds),
    enabled: !!subscriptionIds,
  });
};
const fetchPackages = async (filters) => {
  const url =
    filters.verticalId === LEARN_VERTICAL_ID
      ? new URL(process.env.REACT_APP_OMS_GET_LEARN_PACKAGES_URL)
      : filters.verticalId === KH_VERTICAL_ID
        ? new URL(process.env.REACT_APP_OMS_GET_SCA_PACKAGES_URL)
        : null;

  if (!url) {
    throw new Error("Invalid verticalId provided");
  }
  const rolesString = localStorage.getItem("roles");
  const roles = rolesString
    ? rolesString.split(",").map((role) => role.trim())
    : [];

  const validPageUrls = roles
    .map((roleId) => VALID_SALES_ROLE_IDS[Number(roleId)])
    .find((pageUrl) => pageUrl !== undefined);

  const params = {
    exams: (filters.exams || []).join(","),
    grade: (filters.grade || []).join(","),
    producttype: PRODUCT_TYPES.map((type) => type.value).join(","),
    pageUrl: validPageUrls,
    verticalId: filters.verticalId,
  };

  Object.keys(params).forEach((key) => {
    if (params[key]) {
      url.searchParams.append(key, params[key]);
    }
  });

  const headers = {
    "x-tenant": X_TENANT,
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  const response = await fetch(url, { headers });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Unknown error occurred");
  }

  const data = await response.json();

  if (data.status && data.message === "success") {
    const formattedData = data.data
      .filter((pkg) => pkg.packageId !== null)
      .flatMap((pkg) =>
        pkg.packageSubscriptions.map((subscription) => ({
          id: subscription.packageSubscriptionId,
          verticalId: pkg.verticalId,
          packageId: pkg.packageId,
          displayName: pkg.displayName,
          planName: subscription.planName,
          planValidity: pkg.packageValidityDate,
          mrp: subscription.packagePaymentPlan.mrp,
          booksPrice: calculateBookPrice(
            pkg,
            subscription.packageSubscriptionId,
          ),
          componentProductTypeIds: getAllComponentProductTypeIds(
            pkg,
            subscription,
          ),
        })),
      );
    const subscriptionIds = formattedData.map((item) => item.id);

    if (subscriptionIds.length === 0) {
      throw new Error("No packages found.");
    }
    if (filters.verticalId === LEARN_VERTICAL_ID) {
      return formattedData;
    }
    const paymentHeadsExist = await (async () => {
      try {
        return await fetchPaymentHeadsForSubscription(subscriptionIds);
      } catch (e) {
        console.error("Error during subscription payment heads check:", e);
        throw e;
      }
    })();

    const packagesWithHeads = formattedData.filter((pkg) => {
      return paymentHeadsExist[pkg.id] === true;
    });
    return packagesWithHeads;
  } else {
    throw new Error("Unexpected API response format");
  }
};

export const useFetchPackages = (filters, enabled = true) => {
  return useQuery({
    queryKey: ["packages", filters],
    queryFn: () => fetchPackages(filters),
    enabled,
  });
};

const fetchPostalCodeDetails = async (postalCode) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-postal-code-details/${postalCode}`,
  );

  const headers = {
    "auth-token": getAuthToken(),
    accept: "application/json",
  };

  const response = await fetch(url, { headers });
  const data = await response.json();

  if (!response.ok) {
    throw new Error(`Error: ${data.message}`);
  }

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error("Unexpected API response format");
  }
};

export const useFetchPostalCodeDetails = (postalCode) => {
  const isQueryEnabled = postalCode && postalCode.length === 6;
  return useQuery({
    queryKey: ["postalCodeDetails", postalCode],
    queryFn: () => fetchPostalCodeDetails(postalCode),
    enabled: !!isQueryEnabled,
  });
};

const fetchUpdatedPrice = async (payload) => {
  const url = new URL(`${process.env.REACT_APP_ONE_OMS_URL}/fetch-price`);

  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};

export const useFetchUpdatedPrice = (payload) => {
  return useQuery({
    queryKey: ["discount", payload],
    queryFn: () => fetchUpdatedPrice(payload),
    enabled: false,
    retry: false,
    staleTime: 0,
  });
};

const punchOrder = async (payload) => {
  const url = new URL(`${process.env.REACT_APP_ONE_OMS_URL}/punch-order`);

  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }

  if (data.status === "success") {
    return data.data.order_id;
  }
  throw new Error(
    data?.message ||
      "Oops! Something went wrong. Please refresh the page and try again.",
  );
};
export const usePunchOrder = () => {
  return useMutation({ mutationFn: punchOrder });
};

const fetchCustomerOrderDetails = async (uam_id) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/fetch-customer-orders-details`,
  );

  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const payload = { uam_id };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data.order_details;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};

export const useFetchCustomerOrderDetails = (payload) => {
  const isDisabled = payload.uamId !== null && payload.uamId !== undefined;
  const uam_id = payload.uamId;
  return useQuery({
    queryKey: ["customer_order_details", payload],
    queryFn: () => fetchCustomerOrderDetails(uam_id),
    enabled: !!isDisabled,
  });
};

const fetchSelectedOrderDetails = async (order_id) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-order-info/${order_id}`,
  );

  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const response = await fetch(url, { headers });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data.order_details;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};

export const useSelectedOrderDetails = (order_id) => {
  return useQuery({
    queryKey: ["selected_order_id", order_id],
    queryFn: () => fetchSelectedOrderDetails(order_id),
    enabled: !!order_id,
  });
};

const transformKHUserToUserFormat = (khUser) => {
  return [
    {
      firstName: khUser.first_name,
      lastName: khUser.last_name,
      phone: khUser.phone,
      uamId: khUser.uam_id,
      verticalId: KH_VERTICAL_ID,
    },
  ];
};

const transformLearnUsersWithValidation = (globalUsers) => {
  if (!globalUsers || !Array.isArray(globalUsers.accounts)) return [];

  return globalUsers.accounts.flatMap((account) => {
    if (!Array.isArray(account.profiles)) return [];

    const matchingProfiles = account.profiles.filter(
      (profile) => Number(profile.vertical_id) === Number(LEARN_VERTICAL_ID),
    );

    if (matchingProfiles.length > 1) {
      throw new Error(
        "Seems like there is an issue with this customer's phone number/UAM ID. Please escalate this customer to the support team for resolution.",
      );
    }

    return matchingProfiles.map((profile) => ({
      firstName: account.first_name,
      lastName: account.last_name,
      phone: account.phone,
      verticalId: profile.vertical_id,
      uamId: profile.uam_id,
    }));
  });
};

const fetchCustomerInfo = async (payload) => {
  const { phone_number, uam_id, selectedBusinessVertical } = payload;

  try {
    const urlForLearnUsers = process.env.REACT_APP_ONE_APP_SEARCH_USER_URL;
    const urlForKHUsers = process.env.REACT_APP_GET_VERIFY_USER;

    const payload = phone_number
      ? { isd_code: "+91", phone: phone_number }
      : uam_id
        ? { uam_id }
        : {};

    const headersLearn = {
      accept: "application/json",
      "product-id": "100",
      "tenant-id": "99999",
      platform: "web",
      Authorization: `Bearer ${getAuthToken()}`,
      "Content-Type": "application/json",
    };

    const headersKH = {
      accept: "application/json",
      "product-id": "900",
      "tenant-id": "7",
      platform: "web",
      Authorization: `Bearer ${getAuthToken()}`,
      "Content-Type": "application/json",
    };

    if (String(selectedBusinessVertical) === String(LEARN_VERTICAL_ID)) {
      const response = await fetch(urlForLearnUsers, {
        method: "POST",
        headers: headersLearn,
        body: JSON.stringify(payload),
      });

      const globalUsers = await response.json();

      if (response.status !== 200) {
        throw new Error(
          "An error occurred while requesting Customer Information.",
        );
      }

      const filteredLearnAccounts =
        transformLearnUsersWithValidation(globalUsers);

      if (filteredLearnAccounts.length === 0) {
        throw new Error(
          uam_id
            ? "No user found for the provided UAM ID."
            : "No user found. Please register first.",
        );
      }
      return filteredLearnAccounts;
    }

    const response = await fetch(urlForKHUsers, {
      method: "POST",
      headers: headersKH,
      body: JSON.stringify(payload),
    });

    const khUser = await response.json();

    if (!response.ok) {
      throw new Error(
        "An error occurred while requesting Customer Information.",
      );
    }

    if (!khUser?.users || khUser.users.length === 0) {
      throw new Error("No user found. Please register first.");
    }

    const transformedKHUsers = transformKHUserToUserFormat(khUser.users[0]);

    if (transformedKHUsers.length === 1) {
      localStorage.setItem(
        "selectedUser",
        JSON.stringify(transformedKHUsers[0]),
      );
    }

    return transformedKHUsers;
  } catch (error) {
    console.error("Error fetching KH User:", error.message);
    throw new Error(error.message || "Unexpected error occurred.");
  }
};

export const useFetchCustomerInfo = () => {
  return useMutation({ mutationFn: fetchCustomerInfo });
};

const discardOrder = async (orderId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/discard-order/${orderId}`,
  );

  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: null,
  });

  if (!response.ok) {
    throw new Error(`Failed to discard order: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};

export const useDiscardOrder = () => {
  return useMutation({ mutationFn: discardOrder });
};

const createPaymentLink = async (payload) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/create-payment-link`,
  );
  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (response.ok && data.status === "success") {
    return data.data;
  }

  if (data.detail && Array.isArray(data.detail)) {
    const messages = data.detail.map((err) => err.msg).join("\n");
    throw new Error(messages);
  }
  throw new Error(
    data?.message ||
      "Oops! Something went wrong. Please refresh the page and try again.",
  );
};
export const useCreatePaymentLink = () => {
  return useMutation({ mutationFn: createPaymentLink });
};

const requestCancelOrder = async (payload) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/request-cancellation`,
  );

  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || ` ${response.status}`);
  }

  const data = await response.json();
  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};

export const useRequestCancelOrder = () => {
  return useMutation({ mutationFn: requestCancelOrder });
};

const punchPayment = async (payload) => {
  const url = new URL(`${process.env.REACT_APP_ONE_OMS_URL}/punch-payment-v2`);
  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (response.ok && data.status === "success") {
    return data.data;
  }

  if (data.detail && Array.isArray(data.detail)) {
    const messages = data.detail
      .map((errorDetail) => errorDetail.msg)
      .join("\n");
    const err = new Error(messages);
    if (data.error_code) err.error_code = data.error_code;
    throw err;
  }
  const err = new Error(
    data?.message ||
      "Oops! Something went wrong. Please refresh the page and try again.",
  );
  if (data?.error_code) err.error_code = data.error_code;
  throw err;
};

export const usePunchPayment = () => {
  return useMutation({ mutationFn: punchPayment });
};

const fetchPendingOrders = async (sales_person_id) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-pending-orders-details/${sales_person_id}`,
  );

  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
    body: null,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch pending order: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.status === "success") {
    return data.data.order_details;
  } else {
    throw new Error(
      data.message || "An error occurred while fetching pending orders.",
    );
  }
};

export const useFetchPendingOrders = (sales_person_id) => {
  return useQuery({
    queryKey: ["pendingOrders", sales_person_id],
    queryFn: () => fetchPendingOrders(sales_person_id),
  });
};

export const GRADES = [
  { key: 1, label: "Grade 1", value: "Grade 1" },
  { key: 2, label: "Grade 2", value: "Grade 2" },
  { key: 3, label: "Grade 3", value: "Grade 3" },
  { key: 4, label: "Grade 4", value: "Grade 4" },
  { key: 5, label: "Grade 5", value: "Grade 5" },
  { key: 6, label: "Grade 6", value: "Grade 6" },
  { key: 7, label: "Grade 7", value: "Grade 7" },
  { key: 8, label: "Grade 8", value: "Grade 8" },
  { key: 9, label: "Grade 9", value: "Grade 9" },
  { key: 10, label: "Grade 10", value: "Grade 10" },
  { key: 11, label: "Grade 11", value: "Grade 11" },
  { key: 12, label: "Grade 12", value: "Grade 12" },
  { key: 13, label: "Grade 13", value: "Grade 13" },
];

export const TARGET_EXAMS = [
  { key: 1, label: "FOUNDATIONTEST", value: "FOUNDATIONTEST" },
  { key: 2, label: "NEET", value: "NEET" },
  { key: 3, label: "JEEMAIN", value: "JEEMAIN" },
  { key: 4, label: "JEEMAIN,JEEADVANCED", value: "JEEMAIN,JEEADVANCED" },
  { key: 5, label: "CBSE", value: "CBSE" },
  { key: 6, label: "ICSE", value: "ICSE" },
  { key: 7, label: "Tamil Nadu Board", value: "School Exams (TN Board)" },
  { key: 8, label: "Maharashtra Board", value: "Maharashtra Board" },
  { key: 9, label: "Telangana Board", value: "Telangana Board" },
  { key: 10, label: "Andhra Pradesh Board", value: "Andhra Pradesh Board" },
  {
    key: 11,
    label: "Jammu and Kashmir Board",
    value: "Jammu and Kashmir Board",
  },
  { key: 12, label: "HOTS", value: "HOTS" },
  { key: 13, label: "MATHS CHAMP", value: "MATHS" },
  { key: 14, label: "EEP", value: "EEP" },
  { key: 15, label: "APTITUDETEST", value: "APTITUDETEST" },
  {
    key: 16,
    label: "JEEMAIN,JEEADVANCED,CBSE,NEET",
    value: "JEEMAIN,JEEADVANCED,CBSE,NEET",
  },
  { key: 17, label: "MHCET", value: "MHCET" },
  { key: 18, label: "EAMCET", value: "EAMCET" },
  { key: 19, label: "KCET", value: "KCET" },
];

export const PRODUCT_TYPES = [
  { key: 1, label: "Assessment", value: "Assessment" },
  { key: 2, label: "liveclasses", value: "liveclasses" },
  { key: 3, label: "selflearn", value: "selflearn" },
  { key: 4, label: "Doubts", value: "Doubts" },
  { key: 5, label: "recordedCourse", value: "recordedCourse" },
  { key: 6, label: "Hardware", value: "Hardware" },
  { key: 7, label: "Support", value: "Support" },
];

export const useOrderCancelReasons = () => ({
  isPending: false,
  data: [
    {
      id: 1,
      value: "1",
      label: "Customer Centric",
    },
    {
      id: 2,
      value: "2",
      label: "Sales User Centric",
    },
    {
      id: 3,
      value: "3",
      label: "Finance Partner Centric",
    },
  ].map((type) => ({
    id: type.id,
    value: type.value,
    label: type.label,
  })),
});

const fetchLoanProviderDetails = async (verticalId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-loan-provider-details-by-vertical-id/${verticalId}`,
  );

  const headers = {
    "auth-token": getAuthToken(),
    accept: "application/json",
  };

  const response = await fetch(url, { headers });

  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error("Unexpected API response format");
  }
};

export const useFetchLoanProviderDetails = (verticalId) => {
  return useQuery({
    queryKey: ["loanProviders", verticalId],
    queryFn: () => fetchLoanProviderDetails(verticalId),
    enabled: true,
  });
};

const addNote = async (payload) => {
  const url = new URL(`${process.env.REACT_APP_ONE_OMS_URL}/add-note`);

  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Failed to add note to the order: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};

export const useAddNote = () => {
  return useMutation({ mutationFn: addNote });
};

const fetchPaymentReceipt = async (paymentId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-payment-receipt/${paymentId}`,
  );

  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message ||
        `Failed to fetch payment receipt. Status: ${response.status}`,
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching the payment receipt.",
    );
  }
};

export const useFetchPaymentReceipt = (paymentId) => {
  const query = useQuery({
    queryKey: ["paymentReceipt", paymentId],
    queryFn: () => fetchPaymentReceipt(paymentId),
    enabled: !!paymentId,
  });
  return {
    ...query,
    fetchPaymentReceipt,
  };
};

const fetchOrderSummaryPDF = async (orderId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/generate-or-get-order-summary/${orderId}`,
  );

  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};

export const useFetchOrderSummaryPDF = (orderId) => {
  return useQuery({
    queryKey: ["OrderSummary", orderId],
    queryFn: () => fetchOrderSummaryPDF(orderId),
    enabled: false,
    retry: false,
  });
};

const fetchTaxInvoicePDF = async (orderId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-tax-invoice/${orderId}`,
  );

  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || `Failed to fetch tax invoice.`);
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching tax invoice.",
    );
  }
};

export const useFetchTaxInvoicePDF = (orderId) => {
  const query = useQuery({
    queryKey: ["taxInvoice", orderId],
    queryFn: () => fetchTaxInvoicePDF(orderId),
    enabled: !!orderId,
  });
  return {
    ...query,
    fetchTaxInvoicePDF,
  };
};

const requestTransferOrder = async (payload) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/create-order-modification-request`,
  );

  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || ` ${response.status}`);
  }
  const data = await response.json();

  if (data.status === "success") {
    return data.data.order_proposal_id;
  }
  throw new Error(
    data?.message ||
      "Oops! Something went wrong. Please refresh the page and try again.",
  );
};
export const useRequestTransferOrder = () => {
  return useMutation({ mutationFn: requestTransferOrder });
};

const fetchAllPayments = async (order_id) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-all-payments/${order_id}`,
  );

  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};

export const useFetchAllPayments = (order_id) => {
  return useQuery({
    queryKey: ["order_id", order_id],
    queryFn: () => fetchAllPayments(order_id),
    enabled: !!order_id,
  });
};

const editLoanId = async (payload) => {
  const url = new URL(`${process.env.REACT_APP_ONE_OMS_URL}/edit-loan-id`);
  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};
export const useEditLoanId = () => {
  return useMutation({ mutationFn: editLoanId });
};

const cancelLoan = async (loan_id) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/cancel-loan/${loan_id}`,
  );
  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};
export const useCancelLoan = () => {
  return useMutation({ mutationFn: cancelLoan });
};

const createLoanIntent = async (payload) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/create-loan-intent`,
  );

  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (response.ok && data.status === "success") {
    return data.data;
  }

  if (data.detail && Array.isArray(data.detail)) {
    const messages = data.detail
      .map((errorDetail) => errorDetail.msg)
      .join("\n");
    const err = new Error(messages);
    if (data.error_code) err.error_code = data.error_code;
    throw err;
  }

  const err = new Error(
    data?.message || "Oops! Something went wrong while creating loan intent.",
  );
  if (data?.error_code) err.error_code = data.error_code;
  throw err;
};

export const useCreateLoanIntent = () => {
  return useMutation({ mutationFn: createLoanIntent });
};

const verticalAndTenantsId = async () => {
  const url = new URL(
    `${process.env.REACT_APP_PACKAGE_MANAGEMENT_URL}/get-all-vertical-and-tenants`,
  );
  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_PACKAGE_MANAGEMENT_SECRET_KEY}`,
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};
export const useVerticalAndTenantsId = () => {
  return useQuery({
    queryKey: ["verticalAndTenantsId"],
    queryFn: () => verticalAndTenantsId(),
    staleTime: DAY,
    cacheTime: DAY,
    retry: QUERY_RETRY_ATTEMPTS,
  });
};

const callCustomer = async (payload) => {
  const url = new URL(`${CALL_API_ENDPOINT}`);

  const headers = {
    "Auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();

  if (!response.ok) {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }

  if (data.status === "SUCCESS") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};

export const useCallCustomer = () => {
  return useMutation({ mutationFn: callCustomer });
};

const fetchPartPaymentDetails = async ({ order_id, provider_id }) => {
  const url = `${process.env.REACT_APP_ONE_OMS_URL}/get-part-payment-schedules-v2`;

  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const payload = { order_id, provider_id };

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (response.ok && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message ||
        "An error occurred while fetching part payment schedules.",
    );
  }
};

export const useFetchPartPaymentDetails = (order_id, provider_id) => {
  return useQuery({
    queryKey: ["partPaymentDetails", order_id, provider_id],
    queryFn: () => fetchPartPaymentDetails({ order_id, provider_id }),
    enabled: !!order_id && !!provider_id,
  });
};

const createPartPaymentSchedule = async (payload) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/create-part-payment-schedules-v2`,
  );
  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();
  if (!response.ok) {
    const backendMsg =
      Array.isArray(data?.detail) && data.detail.length > 0
        ? data.detail[0].msg
        : data?.detail?.message;

    throw new Error(backendMsg || data.message);
  }
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};

export const useCreatePartPaymentSchedule = (payload) => {
  return useMutation({
    mutationFn: createPartPaymentSchedule,
  });
};

const fetchPartPaymentInstallmentTaxInvoice = async (installmentId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-installment-tax-invoice/${installmentId}`,
  );

  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || `Failed to fetch tax invoice.`);
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching tax invoice.",
    );
  }
};

export const useFetchPartPaymentInstallmentTaxInvoice = (installmentId) => {
  const query = useQuery({
    queryKey: ["installmentTaxInvoice", installmentId],
    queryFn: () => fetchPartPaymentInstallmentTaxInvoice(installmentId),
    enabled: !!installmentId,
  });
  return {
    ...query,
    fetchPartPaymentInstallmentTaxInvoice,
  };
};

const fetchVerifyKHUser = async (phone_number) => {
  const url = process.env.REACT_APP_GET_VERIFY_USER;

  const payload = { isd_code: "+91", phone: phone_number };

  const headers = {
    accept: "application/json",
    "product-id": "900",
    "tenant-id": "7",
    platform: "web",
    Authorization: getAuthToken(),
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  if (response.status !== 200) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }
  const data = await response.json();
  if (data.length === 0) {
    throw new Error("No user found for the provided phone number or UAM ID.");
  }
  return data;
};

export const useFetchVerifyKHUser = () => {
  return useMutation({ mutationFn: fetchVerifyKHUser });
};

const bankList = async () => {
  const url = new URL(`${process.env.REACT_APP_ONE_OMS_URL}/get-banks-list`);
  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};
export const useBankList = () => {
  return useQuery({
    queryKey: ["bankList"],
    queryFn: () => bankList(),
    staleTime: DAY,
    cacheTime: DAY,
    retry: QUERY_RETRY_ATTEMPTS,
  });
};

const updateCashStatus = async (payload) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/update-cash-status`,
  );
  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};
export const useUpdateCashStatus = () => {
  return useMutation({ mutationFn: updateCashStatus });
};

const updateChequeStatus = async (payload) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/update-cheque-status`,
  );
  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};
export const useUpdateChequeStatus = () => {
  return useMutation({ mutationFn: updateChequeStatus });
};

const checkPhoneNumberMatch = (data) => {
  const storedPhoneNumber = localStorage.getItem("phoneNumber");
  if (storedPhoneNumber && data.phone === storedPhoneNumber) {
    throw new Error("You can not create order for your own ID.");
  }
};

const fetchKHUser = async (uam_id) => {
  const url = process.env.REACT_APP_GET_VERIFY_USER;

  const payload = { uam_id };

  const headers = {
    accept: "application/json",
    "product-id": "900",
    "tenant-id": "7",
    platform: "web",
    Authorization: `Bearer ${getAuthToken()}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();

  if (!response.ok) {
    const backendMsg =
      Array.isArray(data?.detail) && data.detail.length > 0
        ? data.detail[0].msg
        : data?.detail?.message;

    throw new Error(backendMsg || "Something went wrong.");
  }

  if (Array.isArray(data) && data.length === 0) {
    throw new Error("No user found for the provided phone number or UAM ID.");
  }
  checkPhoneNumberMatch(data.users[0]);
  return data.users[0];
};

const transformLearnUsers = (globalUsers) => {
  if (
    !globalUsers ||
    !globalUsers.accounts ||
    globalUsers.accounts.length === 0
  ) {
    return [];
  }

  const account = globalUsers.accounts[0];

  if (!Array.isArray(account.profiles)) {
    return null;
  }

  const matchingProfiles = account.profiles.filter(
    (profile) => Number(profile.vertical_id) === Number(LEARN_VERTICAL_ID),
  );

  if (matchingProfiles.length > 1) {
    throw new Error(
      "Seems like there is an issue with this customer's phone number/UAM ID. Please escalate this customer to the support team for resolution.",
    );
  }

  if (matchingProfiles.length === 1) {
    const profile = matchingProfiles[0];
    return {
      phone: account.phone || "",
      verticalId: profile.vertical_id,
      uamId: profile.uam_id,
    };
  }

  return null;
};

const fetchLearnUser = async (uam_id) => {
  const url = process.env.REACT_APP_ONE_APP_SEARCH_USER_URL;

  const payload = { uam_id };

  const headers = {
    accept: "application/json",
    "product-id": "1000",
    "tenant-id": "1",
    platform: "web",
    Authorization: `Bearer ${getAuthToken()}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  const globalUsers = await response.json();

  if (response.status !== 200) {
    const backendMsg =
      Array.isArray(globalUsers?.detail) && globalUsers.detail.length > 0
        ? globalUsers.detail[0].msg
        : globalUsers?.detail?.message;

    throw new Error(backendMsg || "Something went wrong.");
  }

  const filtered_users_data = transformLearnUsers(globalUsers);

  if (filtered_users_data.length === 0) {
    throw new Error("No user found for the provided UAM ID.");
  }
  checkPhoneNumberMatch(filtered_users_data);
  return filtered_users_data;
};

const fetchUser = async (uamId, verticalId) => {
  if (Number(verticalId) === Number(LEARN_VERTICAL_ID)) {
    return fetchLearnUser(uamId);
  }
  if (Number(verticalId) === Number(KH_VERTICAL_ID)) {
    return fetchKHUser(uamId);
  }
};

export const useFetchUser = (uamId, verticalId) => {
  return useQuery({
    queryKey: [uamId, verticalId],
    queryFn: () => fetchUser(uamId, verticalId),
  });
};

const updateBankTransferStatus = async (payload) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/update-bank-transfer-status`,
  );
  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};
export const useUpdateBankTransferStatus = () => {
  return useMutation({ mutationFn: updateBankTransferStatus });
};

const fetchInvoiceDetails = async (paymentId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-payment-invoices-details/${paymentId}`,
  );

  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};

export const useFetchInvoiceDetails = (paymentId) => {
  return useQuery({
    queryKey: ["InvoiceDetails", paymentId],
    queryFn: () => fetchInvoiceDetails(paymentId),
  });
};

const fetchPaymentTaxInvoice = async (invoiceNumber) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-heads-payment-invoice/${invoiceNumber}`,
  );

  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message ||
        `Failed to fetch tax invoice. Status: ${response.status}`,
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching the tax invoice.",
    );
  }
};

export const useFetchPaymentTaxInvoice = (invoiceNumber) => {
  const query = useQuery({
    queryKey: ["paymentTaxInvoice", invoiceNumber],
    queryFn: () => fetchPaymentTaxInvoice(invoiceNumber),
    enabled: !!invoiceNumber,
  });
  return {
    ...query,
    fetchPaymentTaxInvoice,
  };
};

const fetchOrderPaymentFlows = async (orderId) => {
  const url = `${process.env.REACT_APP_ONE_OMS_URL}/order-part-payments-status/${orderId}`;

  const options = {
    method: "GET",
    headers: {
      accept: "application/json",
      "auth-token": getAuthToken(),
      "Content-Type": "application/json",
    },
  };
  const data = await fetchJSON(url, options);
  return data || [];
};

export const useFetchOrderPaymentFlows = (orderId) => {
  return useQuery({
    queryKey: ["paymentFlows", orderId],
    queryFn: () => fetchOrderPaymentFlows(orderId),
    enabled: !!orderId,
  });
};

const switchPaymentProvider = async (payload) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/switch-part-payment-provider`,
  );
  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};
export const useSwitchPaymentProvider = () => {
  return useMutation({ mutationFn: switchPaymentProvider });
};

const cancelMandate = async (orderId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/cancel-mandate/${orderId}`,
  );

  const headers = {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
  });
  const data = await response.json();
  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};

export const useCancelMandate = () => {
  return useMutation({ mutationFn: cancelMandate });
};

const generateOtp = async (phoneNumberAndCaptcha) => {
  const url = new URL(
    `${process.env.REACT_APP_COMMSERVICE_URL}/api/v3/generate-otp`,
  );
  const headers = {
    accept: "application/json",
    "Content-Type": "application/json",
  };
  const payload = {
    isd_code: INDIAN_ISD_CODE,
    phone: phoneNumberAndCaptcha.phone,
    tenant_id: 1,
    product_id: 300,
    captcha_token: phoneNumberAndCaptcha.captcha_token,
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};

export const useGenerateOtp = () => {
  return useMutation({ mutationFn: generateOtp });
};

const loginWithOtp = async (payload) => {
  const response = await fetch(
    `${process.env.REACT_APP_GATEWAY_URL}/api/v1/user/agents/encrypted-login-with-otp`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        platform: PLATFORM,
        "product-id": Number(LOGIN_PRODUCT_ID),
        "tenant-id": Number(LOGIN_TENANT_ID),
      },
      body: JSON.stringify({
        isd_code: INDIAN_ISD_CODE,
        phone: payload.phone,
        otp: payload.otp,
        product_id: REQUEST_PRODUCT_ID,
        tenant_id: LOGIN_TENANT_ID,
      }),
    },
  );

  if (!response.ok) {
    const errorBody = await response.json();
    const errorMessage = errorBody?.message || "Something went wrong.";
    throw new Error(errorMessage);
  }
  const data = await response.json();

  return data.data;
};

export const useLoginWithOtp = () => useMutation({ mutationFn: loginWithOtp });

const changePassword = async (payload) => {
  const url = new URL(
    `${process.env.REACT_APP_GATEWAY_URL}/api/v1/user/agents/change-password
`,
  );
  const headers = {
    accept: "application/json",
    "product-id": REQUEST_PRODUCT_ID,
    "tenant-id": LOGIN_TENANT_ID,
    platform: PLATFORM,
    Authorization: `Bearer ${getAuthToken()}`,
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorBody = await response.json();
    const errorMessage = errorBody?.message || "Something went wrong.";
    throw new Error(errorMessage);
  }
  const data = await response.json();

  return data.data;
};

export const useChangePassword = () =>
  useMutation({ mutationFn: changePassword });

const getPrimaryContact = (email, phone) => {
  return email && email.trim().length > 0 ? email.trim() : phone;
};

const morphAgentData = (data) => {
  if (!data || !data.data || !Array.isArray(data.data.agents)) {
    return [];
  }
  return data.data.agents.map((agent) => ({
    key: agent.uam_id,
    label: `${agent.first_name} ${agent.last_name} , ${getPrimaryContact(
      agent.email,
      agent.phone,
    )}`,
    value: agent.uam_id,
  }));
};

const fetchSalesAgents = async (searchTerm) => {
  const url = new URL(
    `${process.env.REACT_APP_GATEWAY_URL}/api/v1/user/agents/search-agent`,
  );

  const headers = {
    accept: "application/json",
    "product-id": REQUEST_PRODUCT_ID,
    "tenant-id": LOGIN_TENANT_ID,
    platform: PLATFORM,
    Authorization: `Bearer ${getAuthToken()}`,
    "Content-Type": "application/json",
  };

  const payload = {
    name: searchTerm,
    roles: ROLES_FOR_SEARCHING_AGENT,
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    return [];
  }

  const data = await response.json();

  return morphAgentData(data);
};

export const useSearchSalesAgent = (searchTerm, enabled = true) => {
  const shouldFetch =
    enabled && typeof searchTerm === "string" && searchTerm.length >= 3;

  return useQuery({
    queryKey: ["agents", searchTerm],
    queryFn: () => fetchSalesAgents(searchTerm),
    enabled: shouldFetch,
  });
};
